/**
 * AlertsCentralPage.js - Central de Alertas Unificada
 * Sistema de alertas consolidado com suporte bilingue PT/EN
 * 
 * Funcionalidades:
 * - Follow-ups de clientes
 * - Cobrança de clientes devedores
 * - Pagamentos fixos (renda, água, luz, salários)
 * - Fundo de caixa interno
 * - Dívidas a fornecedores
 * - Alertas de garantias e licenças
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n';
import { 
  Bell, AlertTriangle, Users, CreditCard, Building, Wallet, Truck,
  Plus, CheckCircle, Clock, Phone, MessageSquare, Calendar,
  DollarSign, TrendingDown, AlertCircle, FileText, Key, Shield
} from 'lucide-react';
import { formatCurrency, formatDisplayCode, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { licensesAPI, clientsAPI } from '@/lib/api';
import { getStoredAuthToken } from '@/lib/authStorage';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const alertsAPI = {
  getSummary: () => api.get('/api/alerts/summary'),
  getAllAlerts: (params) => api.get('/api/alerts/all', { params }),
  resolveAlert: (id, data) => api.put(`/api/alerts/${id}/resolve`, data),
  getFollowups: (params) => api.get('/api/alerts/followups', { params }),
  createFollowup: (data) => api.post('/api/alerts/followups', data),
  logContact: (id, notes, nextFollowup) => api.post(`/api/alerts/followups/${id}/contact`, null, { params: { notes, next_followup: nextFollowup } }),
  getClientDebts: (params) => api.get('/api/alerts/debts/clients', { params }),
  createClientDebt: (data) => api.post('/api/alerts/debts/client', data),
  getFixedPayments: (params) => api.get('/api/alerts/fixed-payments', { params }),
  getDueFixedPayments: (days) => api.get('/api/alerts/fixed-payments/due', { params: { days_ahead: days } }),
  createFixedPayment: (data) => api.post('/api/alerts/fixed-payments', data),
  markFixedPaymentPaid: (id, amount, method, ref) => api.post(`/api/alerts/fixed-payments/${id}/pay`, null, { params: { amount, payment_method: method, reference: ref } }),
  getPettyCashConfigs: () => api.get('/api/alerts/petty-cash/config'),
  configurePettyCash: (locationId, locationName, fundAmount, threshold) => 
    api.post('/api/alerts/petty-cash/config', null, { params: { location_id: locationId, location_name: locationName, fund_amount: fundAmount, alert_threshold: threshold } }),
  recordPettyCashExpense: (data) => api.post('/api/alerts/petty-cash/expense', data),
  getPettyCashExpenses: (params) => api.get('/api/alerts/petty-cash/expenses', { params }),
  replenishPettyCash: (locationId, amount) => api.post('/api/alerts/petty-cash/replenish', null, { params: { location_id: locationId, amount } }),
  getSupplierDebts: (params) => api.get('/api/alerts/debts/suppliers', { params }),
};

const priorityColors = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  low: 'bg-blue-100 text-blue-800 border-blue-300',
  warning: 'bg-amber-100 text-amber-800 border-amber-300'
};

const sourceModuleLabels = {
  cash_management: 'Caixa',
  sales: 'Vendas',
  clients: 'Clientes',
  expenses: 'Despesas',
  inventory: 'Inventário',
  alerts: 'Alertas',
};

const formatMaputoDate = (value, language, fallback = '—') => {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat(language === 'pt' ? 'pt-MZ' : 'en-GB', {
    timeZone: 'Africa/Maputo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

// Licenses Alerts Tab Component
function LicensesAlertsTab() {
  const { t, language } = useLanguage();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDays, setFilterDays] = useState('30');

  const filterOptions = ['7', '14', '30', '60', '90'];

  const loadLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await safeRequest(
        () => licensesAPI.getAlertsDashboard({ days: parseInt(filterDays, 10), alert_type: 'license_expiring' }),
        { data: { alerts: [] } }
      );
      setLicenses(response.data?.alerts || []);
    } catch (error) {
      console.error('Failed to load license alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [filterDays]);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="alerts-licenses-title">
          <Key className="h-5 w-5 text-purple-600" />
          {t('alerts.licensesTitle')}
        </h3>
        <Select value={filterDays} onValueChange={setFilterDays}>
          <SelectTrigger className="w-[150px]" data-testid="alerts-licenses-filter-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.map((days) => (
              <SelectItem key={days} value={days}>{t('alerts.nextDays', { count: Number(days) })}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-4">
          {licenses.length === 0 ? (
            <p className="text-center text-muted-foreground py-8" data-testid="alerts-licenses-empty-state">{t('alerts.noLicensesExpiring', { count: Number(filterDays) })}</p>
          ) : (
            <div className="space-y-3" data-testid="alerts-licenses-list">
              {licenses.map(license => (
                <div key={license.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Key className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">{license.name}</p>
                      <p className="text-sm text-muted-foreground">{license.client_name}</p>
                      {license.client_phone && (
                        <p className="text-xs text-blue-600">{license.client_phone}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={cn("mb-1", 
                      license.days_remaining <= 7 ? 'bg-red-100 text-red-700' :
                      license.days_remaining <= 14 ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    )}>
                      {t('alerts.daysRemaining', { count: license.days_remaining })}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {t('alerts.expiresOn', { date: formatMaputoDate(license.expiry_date, language, t('alerts.unknownValue')) })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Warranties Alerts Tab Component
function WarrantiesAlertsTab() {
  const { t, language } = useLanguage();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDays, setFilterDays] = useState('30');

  const filterOptions = ['7', '14', '30', '60', '90'];

  const loadWarranties = useCallback(async () => {
    try {
      setLoading(true);
      const response = await safeRequest(
        () => licensesAPI.getAlertsDashboard({ days: parseInt(filterDays, 10), alert_type: 'warranty_expiring' }),
        { data: { alerts: [] } }
      );
      setWarranties(response.data?.alerts || []);
    } catch (error) {
      console.error('Failed to load warranty alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [filterDays]);

  useEffect(() => {
    loadWarranties();
  }, [loadWarranties]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="alerts-warranties-title">
          <Shield className="h-5 w-5 text-blue-600" />
          {t('alerts.warrantiesTitle')}
        </h3>
        <Select value={filterDays} onValueChange={setFilterDays}>
          <SelectTrigger className="w-[150px]" data-testid="alerts-warranties-filter-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.map((days) => (
              <SelectItem key={days} value={days}>{t('alerts.nextDays', { count: Number(days) })}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-4">
          {warranties.length === 0 ? (
            <p className="text-center text-muted-foreground py-8" data-testid="alerts-warranties-empty-state">{t('alerts.noWarrantiesExpiring', { count: Number(filterDays) })}</p>
          ) : (
            <div className="space-y-3" data-testid="alerts-warranties-list">
              {warranties.map(warranty => (
                <div key={warranty.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{warranty.name}</p>
                      <p className="text-sm text-muted-foreground">{warranty.client_name}</p>
                      {warranty.client_phone && (
                        <p className="text-xs text-blue-600">{warranty.client_phone}</p>
                      )}
                      <p className="text-xs text-muted-foreground">{warranty.category}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={cn("mb-1", 
                      warranty.days_remaining <= 7 ? 'bg-red-100 text-red-700' :
                      warranty.days_remaining <= 14 ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    )}>
                      {t('alerts.daysRemaining', { count: warranty.days_remaining })}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {t('alerts.expiresOn', { date: formatMaputoDate(warranty.expiry_date, language, t('alerts.unknownValue')) })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AlertsCentralPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [clientDebts, setClientDebts] = useState([]);
  const [fixedPayments, setFixedPayments] = useState([]);
  const [duePayments, setDuePayments] = useState([]);
  const [pettyCash, setPettyCash] = useState([]);
  const [supplierDebts, setSupplierDebts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Dialogs
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [fixedPaymentDialogOpen, setFixedPaymentDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [replenishDialogOpen, setReplenishDialogOpen] = useState(false);
  const [payFixedDialogOpen, setPayFixedDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedPettyCash, setSelectedPettyCash] = useState(null);
  
  // Form states
  const [followupForm, setFollowupForm] = useState({ client_name: '', contact_phone: '', followup_date: '', reason: '', notes: '' });
  const [fixedPaymentForm, setFixedPaymentForm] = useState({ name: '', category: 'rent', amount: '', frequency: 'monthly', due_day: '1' });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', category: 'transport', location_id: '', notes: '' });
  const [replenishAmount, setReplenishAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Numerário');
  
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['admin', 'store_manager']);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const priorityLabels = {
    critical: t('alerts.critical'),
    high: t('alerts.high'),
    medium: t('alerts.medium'),
    low: t('alerts.low'),
    warning: t('alerts.high'),
  };
  const paymentMethodOptions = [
    { value: 'Numerário', label: t('alerts.paymentMethodCash') },
    { value: 'M-Pesa', label: t('alerts.paymentMethodMpesa') },
    { value: 'E-MOLA', label: t('alerts.paymentMethodEmola') },
    { value: 'Transferência', label: t('alerts.paymentMethodTransfer') },
  ];
  const getRecurringCategoryLabel = (value) => ({
    rent: t('alerts.categoryRent'),
    water: t('alerts.categoryWater'),
    electricity: t('alerts.categoryElectricity'),
    internet: t('alerts.categoryInternet'),
    salary: t('alerts.categorySalary'),
    other: t('alerts.categoryOther'),
  }[value] || value || t('alerts.unknownValue'));
  const getExpenseCategoryLabel = (value) => ({
    transport: t('alerts.expenseCategoryTransport'),
    maintenance: t('alerts.expenseCategoryMaintenance'),
    supplies: t('alerts.expenseCategorySupplies'),
    other: t('alerts.expenseCategoryOther'),
  }[value] || value || t('alerts.unknownValue'));
  const getFrequencyLabel = (value) => ({
    monthly: t('alerts.frequencyMonthly'),
    weekly: t('alerts.frequencyWeekly'),
    quarterly: t('alerts.frequencyQuarterly'),
    yearly: t('alerts.frequencyYearly'),
  }[value] || value || t('alerts.unknownValue'));
  const formatDate = (value) => formatMaputoDate(value, language, t('alerts.unknownValue'));

  useEffect(() => {
    loadData();
    loadLocationsAndClients();
  }, []);

  const loadLocationsAndClients = async () => {
    try {
      const [locRes, clientsRes] = await Promise.all([
        safeRequest(() => api.get('/api/settings/locations'), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => clientsAPI.list(), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'clients.list', onEvent: captureFetchTelemetry } })
      ]);
      setLocations(locRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Failed to load locations/clients:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const [summaryRes, alertsRes, followupsRes, debtsRes, fixedRes, dueRes, pettyRes, supplierRes] = await Promise.all([
        safeRequest(() => alertsAPI.getSummary(), { data: { total_active: 0, by_priority: {} } }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.summary', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getAllAlerts({ status: 'active', limit: 20 }), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.all', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getFollowups({ status: 'active' }), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.followups', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getClientDebts({ status: 'active' }), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.client-debts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getFixedPayments({}), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.fixed-payments', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getDueFixedPayments(7), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.due-fixed-payments', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getPettyCashConfigs(), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.petty-cash', onEvent: captureFetchTelemetry } }),
        safeRequest(() => alertsAPI.getSupplierDebts({ status: 'active' }), { data: [] }, { telemetry: { module: 'alerts-central', endpoint: 'alerts.supplier-debts', onEvent: captureFetchTelemetry } })
      ]);
      
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data || []);
      setFollowups(followupsRes.data || []);
      setClientDebts(debtsRes.data || []);
      setFixedPayments(fixedRes.data || []);
      setDuePayments(dueRes.data || []);
      setPettyCash(pettyRes.data || []);
      setSupplierDebts(supplierRes.data || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error(t('alerts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResolveAlert = async (alertId, notes) => {
    try {
      await alertsAPI.resolveAlert(alertId, { resolution_notes: notes });
      toast.success(t('alerts.resolveSuccess'));
      loadData();
    } catch (error) {
      toast.error(t('alerts.resolveError'));
    }
  };

  const handleCreateFollowup = async () => {
    if (!followupForm.client_name || !followupForm.followup_date || !followupForm.reason) {
      toast.error(t('alerts.requiredFields'));
      return;
    }
    try {
      await alertsAPI.createFollowup({
        client_id: 'manual',
        client_name: followupForm.client_name,
        contact_phone: followupForm.contact_phone,
        followup_date: followupForm.followup_date,
        reason: followupForm.reason,
        notes: followupForm.notes
      });
      toast.success(t('alerts.followupCreated'));
      setFollowupDialogOpen(false);
      setFollowupForm({ client_name: '', contact_phone: '', followup_date: '', reason: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(t('alerts.followupError'));
    }
  };

  const handleCreateFixedPayment = async () => {
    if (!fixedPaymentForm.name || !fixedPaymentForm.amount) {
      toast.error(t('alerts.requiredFields'));
      return;
    }
    try {
      await alertsAPI.createFixedPayment({
        name: fixedPaymentForm.name,
        category: fixedPaymentForm.category,
        amount: parseFloat(fixedPaymentForm.amount),
        frequency: fixedPaymentForm.frequency,
        due_day: parseInt(fixedPaymentForm.due_day)
      });
      toast.success(t('alerts.fixedPaymentCreated'));
      setFixedPaymentDialogOpen(false);
      setFixedPaymentForm({ name: '', category: 'rent', amount: '', frequency: 'monthly', due_day: '1' });
      loadData();
    } catch (error) {
      toast.error(t('alerts.fixedPaymentError'));
    }
  };

  const handlePayFixed = async () => {
    if (!selectedPayment) return;
    try {
      await alertsAPI.markFixedPaymentPaid(selectedPayment.id, selectedPayment.amount, paymentMethod, null);
      toast.success(t('alerts.paymentRegistered'));
      setPayFixedDialogOpen(false);
      setSelectedPayment(null);
      loadData();
    } catch (error) {
      toast.error(t('alerts.paymentRegisterError'));
    }
  };

  const handleRecordExpense = async () => {
    if (!expenseForm.description || !expenseForm.amount || !expenseForm.location_id) {
      toast.error(t('alerts.requiredFields'));
      return;
    }
    const loc = locations.find(l => l.id === expenseForm.location_id);
    try {
      await alertsAPI.recordPettyCashExpense({
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        category: expenseForm.category,
        location_id: expenseForm.location_id,
        location_name: loc?.name || 'N/A',
        notes: expenseForm.notes
      });
      toast.success(t('alerts.expenseRecorded'));
      setExpenseDialogOpen(false);
      setExpenseForm({ description: '', amount: '', category: 'transport', location_id: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('alerts.expenseRecordError'));
    }
  };

  const handleReplenish = async () => {
    if (!selectedPettyCash || !replenishAmount) return;
    try {
      await alertsAPI.replenishPettyCash(selectedPettyCash.location_id, parseFloat(replenishAmount));
      toast.success(t('alerts.fundReplenished'));
      setReplenishDialogOpen(false);
      setSelectedPettyCash(null);
      setReplenishAmount('');
      loadData();
    } catch (error) {
      toast.error(t('alerts.fundReplenishError'));
    }
  };

  const hasAlertsData = Boolean(
    summary?.total_active ||
    alerts.length ||
    followups.length ||
    clientDebts.length ||
    fixedPayments.length ||
    duePayments.length ||
    pettyCash.length ||
    supplierDebts.length ||
    locations.length ||
    clients.length
  );

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasAlertsData}
      partialIssues={partialIssues}
      loadingTestId="alerts-central-loading-state"
      partialIssuesTestId="alerts-central-partial-issues"
      emptyState={
        <div className="p-6 space-y-6" data-testid="alerts-central-page">
          <EmptyStatePanel
            title={t('alerts.emptyTitle')}
            description={t('alerts.emptyDescription')}
            actionLabel={t('actions.refresh')}
            onAction={() => {
              loadData();
              loadLocationsAndClients();
            }}
            testId="alerts-central-empty-state"
          />
        </div>
      }
    >
    <div className="p-6 space-y-6" data-testid="alerts-central-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
            <Bell className="h-8 w-8 text-orange-500" />
            {t('alerts.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('alerts.strategicControl')}
          </p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Card className={cn("border-l-4", summary?.by_priority?.critical > 0 ? "border-l-red-500" : "border-l-green-500")}>
          <CardContent className="pt-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-muted-foreground">{t('alerts.totalActive')}</p>
                <p className="text-2xl font-bold">{summary?.total_active || 0}</p>
              </div>
              <AlertTriangle className={cn("h-8 w-8", summary?.by_priority?.critical > 0 ? "text-red-500" : "text-green-500")} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t('alerts.critical')}</p>
            <p className="text-2xl font-bold text-red-600">{summary?.by_priority?.critical || 0}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t('alerts.high')}</p>
            <p className="text-2xl font-bold text-orange-600">{summary?.by_priority?.high || 0}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t('alerts.fixedPaymentsThisWeek')}</p>
            <p className="text-2xl font-bold text-amber-600">{summary?.fixed_payments_due_this_week || 0}</p>
            <p className="text-xs text-muted-foreground">{t('alerts.thisWeek')}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t('alerts.pettyCash')}</p>
            <div className="space-y-1">
              {(summary?.petty_cash || []).slice(0, 2).map(pc => (
                <div key={pc.location} className="flex justify-between text-xs">
                  <span className="truncate">{pc.location?.slice(0, 10)}</span>
                  <Badge className={cn("text-xs", pc.is_low ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700")}>
                    {pc.percentage}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">
          <TabsTrigger value="overview" data-testid="tab-overview">{t('alerts.overview')}</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups">{t('alerts.followups')}</TabsTrigger>
          <TabsTrigger value="debts" data-testid="tab-debts">{t('alerts.debtors')}</TabsTrigger>
          <TabsTrigger value="fixed" data-testid="tab-fixed">{t('alerts.fixedPayments')}</TabsTrigger>
          <TabsTrigger value="petty" data-testid="tab-petty">{t('alerts.pettyCash')}</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">{t('alerts.suppliers')}</TabsTrigger>
          <TabsTrigger value="licenses" data-testid="tab-licenses">{t('alerts.licenses')}</TabsTrigger>
          <TabsTrigger value="warranties" data-testid="tab-warranties">{t('alerts.warranties')}</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">{t('alerts.allAlerts')}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid gap-6 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  {t('alerts.criticalAlerts')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts.filter(a => a.priority === 'critical').length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('alerts.noCriticalAlerts')}</p>
                ) : (
                  <div className="space-y-2">
                    {alerts.filter(a => a.priority === 'critical').slice(0, 5).map(alert => (
                      <div key={alert.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="font-medium text-sm">{alert.message || alert.reason || alert.client_name || t('alerts.activeAlertFallback')}</p>
                        <p className="text-xs text-muted-foreground mt-1">{sourceModuleLabels[alert.source_module] || alert.source_module} · {alert.alert_type}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t('alerts.dueThisWeek')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {duePayments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('alerts.noPendingPayments')}</p>
                ) : (
                  <div className="space-y-2">
                    {duePayments.slice(0, 5).map(payment => (
                      <div key={payment.id} className="flex justify-between items-center p-2 bg-amber-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{payment.name}</p>
                          <p className="text-xs text-muted-foreground">{getRecurringCategoryLabel(payment.category)}</p>
                        </div>
                        <p className="font-bold">{formatCurrency(payment.amount)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Alertas activos rastreáveis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4" data-testid="alerts-overview-module-breakdown">
                {Object.entries(summary?.by_source_module || {}).length > 0 ? Object.entries(summary.by_source_module).map(([moduleKey, count]) => (
                  <Badge key={moduleKey} variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                    {sourceModuleLabels[moduleKey] || moduleKey}: {count}
                  </Badge>
                )) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Sem alertas activos</Badge>
                )}
              </div>

              {alerts.length === 0 ? (
                <p className="text-center text-muted-foreground py-4" data-testid="alerts-overview-empty-state">{t('alerts.noActiveAlerts')}</p>
              ) : (
                <div className="space-y-2" data-testid="alerts-overview-active-list">
                  {alerts.slice(0, 5).map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                      <div>
                        <p className="font-medium text-sm">{alert.message || alert.reason || alert.client_name || t('alerts.activeAlertFallback')}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {sourceModuleLabels[alert.source_module] || alert.source_module} · {alert.alert_type} · {alert.source_record_label || alert.source_record_id}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => navigate(alert.source_route || '/alerts')} data-testid={`alerts-overview-open-source-${String(alert.id).replace(/[^a-zA-Z0-9-_]/g, '-')}`}>
                        Abrir origem
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow-ups Tab */}
        <TabsContent value="followups" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold" data-testid="alerts-followups-title">{t('alerts.followupRequestsTitle')}</h3>
            <Button onClick={() => setFollowupDialogOpen(true)} data-testid="new-followup-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('alerts.newFollowupButton')}
            </Button>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {followups.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="alerts-followups-empty-state">{t('alerts.noFollowups')}</p>
              ) : (
                <div className="space-y-3" data-testid="alerts-followups-list">
                  {followups.map(f => (
                    <div key={f.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{f.client_name}</p>
                          <p className="text-sm text-muted-foreground">{f.reason}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(f.followup_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {f.contact_phone && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={`tel:${f.contact_phone}`} data-testid={`alerts-followup-call-${f.id}`}>
                              <Phone className="h-4 w-4 mr-1" />
                              {t('alerts.call')}
                            </a>
                          </Button>
                        )}
                        <Button size="sm" onClick={() => handleResolveAlert(f.id, 'Contactado')} data-testid={`alerts-followup-resolve-${f.id}`}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t('alerts.resolved')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debts Tab */}
        <TabsContent value="debts" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold" data-testid="alerts-debtors-title">{t('alerts.debtorsTitle')}</h3>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {clientDebts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="alerts-debtors-empty-state">{t('alerts.noDebtors')}</p>
              ) : (
                <div className="space-y-3" data-testid="alerts-debtors-list">
                  {clientDebts.map(d => (
                    <div key={d.id} className="flex justify-between items-center p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{d.client_name}</p>
                        <p className="text-sm text-muted-foreground">{t('alerts.saleLabel', { value: d.sale_number ? formatDisplayCode(d.sale_number, 'SAL') : t('alerts.unknownValue') })}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge className={cn("text-xs", priorityColors[d.priority])}>
                            {priorityLabels[d.priority]}
                          </Badge>
                          {d.days_overdue > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {d.days_overdue} {t('alerts.daysOverdue')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-600">{formatCurrency(d.amount_due - (d.amount_paid || 0))}</p>
                        <p className="text-xs text-muted-foreground">{t('alerts.fromAmount', { value: formatCurrency(d.amount_due) })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fixed Payments Tab */}
        <TabsContent value="fixed" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold" data-testid="alerts-fixed-payments-title">{t('alerts.fixedPaymentsTitle')}</h3>
            <Button onClick={() => setFixedPaymentDialogOpen(true)} data-testid="new-fixed-payment-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('alerts.newFixedPayment')}
            </Button>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {fixedPayments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="alerts-fixed-payments-empty-state">{t('alerts.noFixedPaymentsConfigured')}</p>
              ) : (
                <div className="space-y-3" data-testid="alerts-fixed-payments-list">
                  {fixedPayments.map(p => (
                    <div key={p.id} className="flex justify-between items-center p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          {p.category === 'rent' && <Building className="h-5 w-5 text-purple-600" />}
                          {p.category === 'salary' && <Users className="h-5 w-5 text-purple-600" />}
                          {!['rent', 'salary'].includes(p.category) && <FileText className="h-5 w-5 text-purple-600" />}
                        </div>
                        <div>
                          <p className="font-medium">{p.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{getRecurringCategoryLabel(p.category)} • {getFrequencyLabel(p.frequency)}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('alerts.nextDue', { date: p.next_due_date ? formatDate(p.next_due_date) : t('alerts.unknownValue') })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">{formatCurrency(p.amount)}</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-1"
                          onClick={() => {
                            setSelectedPayment(p);
                            setPayFixedDialogOpen(true);
                          }}
                          data-testid={`alerts-fixed-payment-mark-paid-${p.id}`}
                        >
                          {t('alerts.markPaidButton')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Petty Cash Tab */}
        <TabsContent value="petty" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold" data-testid="alerts-petty-cash-title">{t('alerts.pettyCashTitle')}</h3>
            <Button onClick={() => setExpenseDialogOpen(true)} data-testid="new-expense-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('alerts.recordExpenseButton')}
            </Button>
          </div>
          
          {pettyCash.length === 0 ? (
            <Card>
              <CardContent className="pt-4">
                <p className="text-center text-muted-foreground py-8" data-testid="alerts-petty-cash-empty-state">{t('alerts.noPettyCash')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="alerts-petty-cash-list">
              {pettyCash.map(pc => (
                <Card key={pc.location_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{pc.location_name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(pc.current_balance)}</div>
                    <p className="text-xs text-muted-foreground">{t('alerts.ofAmount', { value: formatCurrency(pc.fund_amount) })}</p>
                    <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={cn(
                          "h-full",
                          (pc.current_balance / pc.fund_amount) > 0.5 ? "bg-green-500" :
                          (pc.current_balance / pc.fund_amount) > 0.2 ? "bg-amber-500" : "bg-red-500"
                        )}
                        style={{ width: `${Math.min(100, (pc.current_balance / pc.fund_amount) * 100)}%` }}
                      />
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full mt-3"
                      onClick={() => {
                        setSelectedPettyCash(pc);
                        setReplenishDialogOpen(true);
                      }}
                      data-testid={`alerts-petty-cash-replenish-${pc.location_id}`}
                    >
                      {t('alerts.replenishFundButton')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold" data-testid="alerts-supplier-debts-title">{t('alerts.supplierDebtsTitle')}</h3>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {supplierDebts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="alerts-supplier-debts-empty-state">{t('alerts.noSupplierDebts')}</p>
              ) : (
                <div className="space-y-3" data-testid="alerts-supplier-debts-list">
                  {supplierDebts.map(d => (
                    <div key={d.id} className="flex justify-between items-center p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <Truck className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium">{d.supplier_name}</p>
                          <p className="text-sm text-muted-foreground">{t('alerts.poLabel', { value: d.po_number || t('alerts.unknownValue') })}</p>
                          <Badge className={cn("text-xs", priorityColors[d.priority])}>
                            {priorityLabels[d.priority]}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-red-600">{formatCurrency(d.amount_due - (d.amount_paid || 0))}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('alerts.dueDateLabel', { date: formatDate(d.due_date) })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Licenses Tab */}
        <TabsContent value="licenses" className="space-y-4 mt-4">
          <LicensesAlertsTab />
        </TabsContent>

        {/* Warranties Tab */}
        <TabsContent value="warranties" className="space-y-4 mt-4">
          <WarrantiesAlertsTab />
        </TabsContent>

        {/* All Alerts Tab */}
        <TabsContent value="all" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('alerts.allActiveAlertsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4" data-testid="alerts-all-module-breakdown">
                {Object.entries(summary?.by_source_module || {}).length > 0 ? Object.entries(summary.by_source_module).map(([moduleKey, count]) => (
                  <Badge key={moduleKey} variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                    {sourceModuleLabels[moduleKey] || moduleKey}: {count}
                  </Badge>
                )) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Sem alertas activos</Badge>
                )}
              </div>
              {alerts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="alerts-all-empty-state">{t('alerts.noActiveAlerts')}</p>
              ) : (
                <div className="space-y-2" data-testid="alerts-all-list">
                  {alerts.map(a => (
                    <div key={a.id} className="flex justify-between items-center p-3 border rounded">
                      <div className="flex items-center gap-3">
                        <Badge className={cn("text-xs", priorityColors[a.priority])}>
                          {priorityLabels[a.priority]}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{sourceModuleLabels[a.source_module] || a.source_module} · {a.alert_type}</p>
                          <p className="text-xs text-muted-foreground">{a.message || a.client_name || a.supplier_name || t('alerts.activeAlertFallback')}</p>
                          <p className="text-xs text-muted-foreground mt-1">Origem: {a.source_record_label || a.source_record_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(a.source_route || '/alerts')} data-testid={`alerts-open-source-${String(a.id).replace(/[^a-zA-Z0-9-_]/g, '-')}`}>
                          Abrir origem
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleResolveAlert(a.id, 'Resolvido')} data-testid={`alerts-resolve-${String(a.id).replace(/[^a-zA-Z0-9-_]/g, '-')}`}>
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Follow-up Dialog */}
      <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
        <DialogContent data-testid="alerts-followup-dialog">
          <DialogHeader>
            <DialogTitle>{t('alerts.followupDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="alerts-followup-client-name">{t('alerts.clientName')} *</Label>
              <Input 
                id="alerts-followup-client-name"
                value={followupForm.client_name}
                onChange={(e) => setFollowupForm({...followupForm, client_name: e.target.value})}
                placeholder={t('alerts.followupNamePlaceholder')}
                data-testid="alerts-followup-client-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-followup-phone">{t('alerts.contactPhone')}</Label>
              <Input 
                id="alerts-followup-phone"
                value={followupForm.contact_phone}
                onChange={(e) => setFollowupForm({...followupForm, contact_phone: e.target.value})}
                placeholder="+258 84 xxx xxxx"
                data-testid="alerts-followup-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-followup-date">{t('alerts.followupDate')} *</Label>
              <Input 
                id="alerts-followup-date"
                type="date"
                value={followupForm.followup_date}
                onChange={(e) => setFollowupForm({...followupForm, followup_date: e.target.value})}
                data-testid="alerts-followup-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-followup-reason">{t('alerts.reason')} *</Label>
              <Input 
                id="alerts-followup-reason"
                value={followupForm.reason}
                onChange={(e) => setFollowupForm({...followupForm, reason: e.target.value})}
                placeholder={t('alerts.followupReasonPlaceholder')}
                data-testid="alerts-followup-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-followup-notes">{t('labels.notes')}</Label>
              <Textarea 
                id="alerts-followup-notes"
                value={followupForm.notes}
                onChange={(e) => setFollowupForm({...followupForm, notes: e.target.value})}
                placeholder={t('labels.notes')}
                rows={2}
                data-testid="alerts-followup-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowupDialogOpen(false)} data-testid="alerts-followup-cancel-button">{t('actions.cancel')}</Button>
            <Button onClick={handleCreateFollowup} data-testid="alerts-followup-submit-button">{t('alerts.createFollowup')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fixed Payment Dialog */}
      <Dialog open={fixedPaymentDialogOpen} onOpenChange={setFixedPaymentDialogOpen}>
        <DialogContent data-testid="alerts-fixed-payment-dialog">
          <DialogHeader>
            <DialogTitle>{t('alerts.fixedPaymentDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="alerts-fixed-payment-name">{t('alerts.paymentName')} *</Label>
              <Input 
                id="alerts-fixed-payment-name"
                value={fixedPaymentForm.name}
                onChange={(e) => setFixedPaymentForm({...fixedPaymentForm, name: e.target.value})}
                placeholder={t('alerts.fixedPaymentNamePlaceholder')}
                data-testid="alerts-fixed-payment-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('alerts.paymentCategory')}</Label>
              <Select value={fixedPaymentForm.category} onValueChange={(v) => setFixedPaymentForm({...fixedPaymentForm, category: v})}>
                <SelectTrigger data-testid="alerts-fixed-payment-category-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">{t('alerts.categoryRent')}</SelectItem>
                  <SelectItem value="water">{t('alerts.categoryWater')}</SelectItem>
                  <SelectItem value="electricity">{t('alerts.categoryElectricity')}</SelectItem>
                  <SelectItem value="internet">{t('alerts.categoryInternet')}</SelectItem>
                  <SelectItem value="salary">{t('alerts.categorySalary')}</SelectItem>
                  <SelectItem value="other">{t('alerts.categoryOther')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-fixed-payment-amount">{t('alerts.paymentAmount')} (MZN) *</Label>
              <Input 
                id="alerts-fixed-payment-amount"
                type="number"
                value={fixedPaymentForm.amount}
                onChange={(e) => setFixedPaymentForm({...fixedPaymentForm, amount: e.target.value})}
                placeholder="0.00"
                data-testid="alerts-fixed-payment-amount-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('alerts.paymentFrequency')}</Label>
                <Select value={fixedPaymentForm.frequency} onValueChange={(v) => setFixedPaymentForm({...fixedPaymentForm, frequency: v})}>
                  <SelectTrigger data-testid="alerts-fixed-payment-frequency-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t('alerts.frequencyMonthly')}</SelectItem>
                    <SelectItem value="weekly">{t('alerts.frequencyWeekly')}</SelectItem>
                    <SelectItem value="quarterly">{t('alerts.frequencyQuarterly')}</SelectItem>
                    <SelectItem value="yearly">{t('alerts.frequencyYearly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="alerts-fixed-payment-due-day">{t('alerts.dueDay')}</Label>
                <Input 
                  id="alerts-fixed-payment-due-day"
                  type="number"
                  min="1"
                  max="28"
                  value={fixedPaymentForm.due_day}
                  onChange={(e) => setFixedPaymentForm({...fixedPaymentForm, due_day: e.target.value})}
                  data-testid="alerts-fixed-payment-due-day-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixedPaymentDialogOpen(false)} data-testid="alerts-fixed-payment-cancel-button">{t('actions.cancel')}</Button>
            <Button onClick={handleCreateFixedPayment} data-testid="alerts-fixed-payment-submit-button">{t('alerts.createFixedPayment')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Fixed Payment Dialog */}
      <Dialog open={payFixedDialogOpen} onOpenChange={setPayFixedDialogOpen}>
        <DialogContent data-testid="alerts-pay-fixed-dialog">
          <DialogHeader>
            <DialogTitle>{t('alerts.payFixedDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium">{selectedPayment?.name}</p>
              <p className="text-2xl font-bold mt-2">{formatCurrency(selectedPayment?.amount || 0)}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('labels.payment')}</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="alerts-pay-fixed-method-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {paymentMethodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFixedDialogOpen(false)} data-testid="alerts-pay-fixed-cancel-button">{t('actions.cancel')}</Button>
            <Button onClick={handlePayFixed} data-testid="alerts-pay-fixed-submit-button">{t('alerts.confirmPayment')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent data-testid="alerts-expense-dialog">
          <DialogHeader>
            <DialogTitle>{t('alerts.expenseDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('labels.location')} *</Label>
              <Select value={expenseForm.location_id} onValueChange={(v) => setExpenseForm({...expenseForm, location_id: v})}>
                <SelectTrigger data-testid="alerts-expense-location-trigger"><SelectValue placeholder={t('alerts.expenseLocationPlaceholder')} /></SelectTrigger>
                <SelectContent>
                  {locations.filter(l => l.type === 'store').map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-expense-description">{t('alerts.expenseDescription')} *</Label>
              <Input 
                id="alerts-expense-description"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                placeholder={t('alerts.expenseDescriptionPlaceholder')}
                data-testid="alerts-expense-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-expense-amount">{t('alerts.paymentAmount')} (MZN) *</Label>
              <Input 
                id="alerts-expense-amount"
                type="number"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                placeholder="0.00"
                data-testid="alerts-expense-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('alerts.expenseCategory')}</Label>
              <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                <SelectTrigger data-testid="alerts-expense-category-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transport">{t('alerts.expenseCategoryTransport')}</SelectItem>
                  <SelectItem value="maintenance">{t('alerts.expenseCategoryMaintenance')}</SelectItem>
                  <SelectItem value="supplies">{t('alerts.expenseCategorySupplies')}</SelectItem>
                  <SelectItem value="other">{t('alerts.expenseCategoryOther')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-expense-notes">{t('labels.notes')}</Label>
              <Textarea 
                id="alerts-expense-notes"
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                placeholder={t('labels.notes')}
                rows={2}
                data-testid="alerts-expense-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)} data-testid="alerts-expense-cancel-button">{t('actions.cancel')}</Button>
            <Button onClick={handleRecordExpense} data-testid="alerts-expense-submit-button">{t('alerts.recordExpense')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replenish Petty Cash Dialog */}
      <Dialog open={replenishDialogOpen} onOpenChange={setReplenishDialogOpen}>
        <DialogContent data-testid="alerts-replenish-dialog">
          <DialogHeader>
            <DialogTitle>{t('alerts.replenishDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium">{selectedPettyCash?.location_name}</p>
              <p className="text-sm text-muted-foreground">
                {t('alerts.currentBalance', { value: formatCurrency(selectedPettyCash?.current_balance || 0) })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('alerts.totalFund', { value: formatCurrency(selectedPettyCash?.fund_amount || 0) })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="alerts-replenish-amount">{t('alerts.replenishAmountLabel')}</Label>
              <Input 
                id="alerts-replenish-amount"
                type="number"
                value={replenishAmount}
                onChange={(e) => setReplenishAmount(e.target.value)}
                placeholder={((selectedPettyCash?.fund_amount || 0) - (selectedPettyCash?.current_balance || 0)).toString()}
                data-testid="alerts-replenish-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplenishDialogOpen(false)} data-testid="alerts-replenish-cancel-button">{t('actions.cancel')}</Button>
            <Button onClick={handleReplenish} data-testid="alerts-replenish-submit-button">{t('alerts.replenishFund')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}
